import { Box } from "@material-ui/core";
import React, { FC, useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useRecoilState } from "recoil";

import { CustomForm } from "@/components/Form";
import { RegistrationPhases } from "@/components/registration/RegistrationPhases";
import { PickerField } from "@/components/selection/PickerField";
import { RegistrationFlowLayout as RegistrationTemplate, RegistrationActions } from "@/layouts/RegistrationFlowLayout";
import { createRequiredMessage } from "@/validations/message";
import { UserRegistrationsAccoutFormState } from "@/view_models/atoms";

interface RegisterFinancialAssetsPageProps {
  totalSteps: number;
  currentStep: number;
  onNext: () => void;
  onBack: () => void;
  validate: () => Promise<boolean>;
}

const loan_ranges = [
  "なし",
  "〜500万円",
  "500〜1,000万円",
  "1,000〜2,000万円",
  "2,000〜3,000万円",
  "3,000〜5,000万円",
  "5,000万円〜1億円",
  "1〜2億円",
  "2〜3億円",
  "3〜5億円",
  "5〜10億円",
  "10億円〜",
];

const RegisterFinancialAssetsPage: FC<RegisterFinancialAssetsPageProps> = ({
  totalSteps,
  currentStep,
  onNext,
  onBack,
  validate,
}) => {
  const {
    control,
    formState: { errors },
    setError,
    watch,
  } = useFormContext();

  const [formData] = useRecoilState(UserRegistrationsAccoutFormState);
  const showOwnershipFields = formData.has_investment_experience !== false;
  const financial_asset = watch("financial_asset");

  const handleNext = async () => {
    const isValid = await validate();

    // 条件付きバリデーション: financial_assetが「なし」以外の場合、real_estate_loan_amountは必須
    const real_estate_loan_amount = control.getValues("real_estate_loan_amount");
    if (financial_asset !== "なし" && (!real_estate_loan_amount || real_estate_loan_amount.trim() === "")) {
      setError("real_estate_loan_amount", {
        type: "manual",
        message: createRequiredMessage("投資用不動産ローン合計"),
      });
      return;
    }

    if (isValid) {
      onNext();
    }
  };

  // formDataが存在する場合に初期値を設定
  useEffect(() => {
    control.setValue("financial_asset", formData.financial_asset);
    control.setValue("holding_property_count", formData.holding_property_count);
    control.setValue("real_estate_loan_amount", formData.real_estate_loan_amount);
  }, []);

  const loanRangeOptions = loan_ranges.map((loanRange) => ({ value: loanRange, label: loanRange }));

  return (
    <RegistrationTemplate
      phaseTags={<RegistrationPhases completed />}
      title={"現在の資産について\n教えてください"}
      totalSteps={totalSteps}
      currentStep={currentStep}
      description="入力内容を元に最適な情報を提供します"
      descriptionSize="small"
    >
      <Box>
        <Controller
          name="financial_asset"
          control={control}
          render={({ ...field }) => (
            <PickerField
              {...field}
              label="金融資産（預金・株式など）"
              error={errors.financial_asset?.message}
              onChange={field.onChange}
              options={loanRangeOptions}
            />
          )}
        />

        {showOwnershipFields && (
          <>
            <Controller
              name="holding_property_count"
              control={control}
              defaultValue={""}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label="投資用不動産保有数"
                  helperText={"※住宅ローンで組んでいる物件は含みません\n　未保有の場合は0を入力してください"}
                  error={!!errors.holding_property_count}
                  errorText={errors.holding_property_count?.message}
                  onChange={field.onChange}
                  placeHolder="入力してください"
                  unitSuffix="件"
                  formatNumeric
                />
              )}
            />
            <Controller
              name="real_estate_loan_amount"
              control={control}
              defaultValue={""}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label="投資用不動産ローン合計"
                  helperText={"※住宅ローン残債は含みません\n　未保有の場合は0を入力してください"}
                  error={!!errors.real_estate_loan_amount}
                  errorText={errors.real_estate_loan_amount?.message}
                  onChange={field.onChange}
                  placeHolder="入力してください"
                  unitSuffix="万円"
                  formatNumeric
                />
              )}
            />
          </>
        )}
      </Box>
      <RegistrationActions onNext={handleNext} onBack={onBack} />
    </RegistrationTemplate>
  );
};

export default RegisterFinancialAssetsPage;
